import React, { useEffect, useState, useCallback } from 'react';
import { convertToKg as convertToKgOrLitre } from '../utils';
import printRecipePDF from '../utils/printRecipePDF';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { productsAPI, trialsAPI, itemsAPI, settingsAPI } from '../api';
import CostSheet from './CostSheet';
import TrialMatrix from './TrialMatrix';
import ItemSearchBox from '../components/ItemSearchBox';
import RecipeModal from '../components/RecipeModal';
import DirectRecipeCreate from '../components/DirectRecipeCreate';
import RecipeTab from '../components/RecipeTab';
import NutritionTab from '../components/NutritionTab';
import NutriScoreTab from '../components/NutriScoreTab';
import FlavorProfileTab from '../components/FlavorProfileTab';
import ProductPdfDialog from '../components/ProductPdfDialog';

const STATUS_LABELS = {
  trial: 'Trial', recipe_confirmed: 'Recipe Confirmed',
  commercial_trial: 'Commercial Trial', launched: 'Launched',
};


// Reusable item search component

// Unit conversion for recipe cost calculation
export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [parentFG, setParentFG] = useState(null);
  const [sfgLinks, setSfgLinks] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [trialView, setTrialView] = useState('detail');
  const [deletedTrials, setDeletedTrials] = useState([]);
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [trials, setTrials] = useState([]);
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [specSheets, setSpecSheets] = useState([]);
  const [samples, setSamples] = useState([]);
  const [recipe, setRecipe] = useState(null);
  const [showPdfDialog, setShowPdfDialog] = useState(false);
  const [itemMaster, setItemMaster] = useState([]);
  const [productStatuses, setProductStatuses] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('trials');
  const [showAddVersion, setShowAddVersion] = useState(false);
  const [showAddTrial, setShowAddTrial] = useState(false);
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [convertMode, setConvertMode] = useState(null); // null | 'in_place' | 'copy'
  const [convertFgSearch, setConvertFgSearch] = useState('');
  const [convertFgId, setConvertFgId] = useState(null);
  const [convertFgName, setConvertFgName] = useState('');
  const [convertPct, setConvertPct] = useState('');
  const [allFgProducts, setAllFgProducts] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [showLinkFG, setShowLinkFG] = useState(false);
  const [showCreateSFG, setShowCreateSFG] = useState(false);
  const [createSFGName, setCreateSFGName] = useState('');
  const [createSFGPct, setCreateSFGPct] = useState('');
  const [linkFGSearch, setLinkFGSearch] = useState('');
  const [linkFGResults, setLinkFGResults] = useState([]);
  const [linkFGSelected, setLinkFGSelected] = useState(null);
  const [linkFGMode, setLinkFGMode] = useState('share');
  const [linkFGPct, setLinkFGPct] = useState('');
  const [versionForm, setVersionForm] = useState({ version_number: '', notes: '' });
  const [trialForm, setTrialForm] = useState({ trial_number: '', date: new Date().toISOString().split('T')[0], notes: '', status: 'ongoing' });
  const [materialForm, setMaterialForm] = useState({ item_code: '', item_name: '', quantity: '', unit: '', unit_cost: '' });
  const [itemSearch, setItemSearch] = useState('');
  const [itemResults, setItemResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [editProductForm, setEditProductForm] = useState({});
  const [depts, setDepts] = useState([]);
  const [allProjects, setAllProjects] = useState([]);

  const loadProduct = useCallback(() => {
    import('axios').then(m => m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' })
      .get(`/products/${id}`)
      .then(r => {
        const p = r.data;
        setProduct(p);
        if (p.is_sfg) import("axios").then(m => m.default.create({ baseURL: process.env.NODE_ENV === "production" ? "/api" : "http://localhost:3001/api" }).get("/products/"+p.id+"/parent-fg").then(r => setParentFG(r.data)));
        import("axios").then(m => m.default.create({ baseURL: process.env.NODE_ENV === "production" ? "/api" : "http://localhost:3001/api" }).get("/products/"+p.id+"/recipe").then(rr => { if(rr.data && rr.data.id) return m.default.create({ baseURL: process.env.NODE_ENV === "production" ? "/api" : "http://localhost:3001/api" }).get("/recipes/"+rr.data.id+"/sfg-links").then(r => setSfgLinks(r.data)); }));
        setEditProductForm({ product_code: p.product_code, name: p.name, department: p.department, internal_group: p.internal_group, product_type: p.product_type || '', status: p.status, description: p.description || '', category: p.category || '', sub_category: p.sub_category || '' });
      }));
  }, [id]);

  const loadVersions = useCallback(() => {
    productsAPI.getVersions(id).then(r => {
      setVersions(r.data);
      if (r.data.length > 0 && !selectedVersion) setSelectedVersion(r.data[r.data.length - 1]);
    });
  }, [id]);

  useEffect(() => {
    loadProduct();
    loadVersions();
    productsAPI.getRecipe(id).then(r => setRecipe(r.data));
    itemsAPI.getForRecipes().then(r => setItemMaster(r.data));
    settingsAPI.getType('product_status').then(r => setProductStatuses(r.data));
    settingsAPI.getType('product_type').then(r => setProductTypes(r.data));
    settingsAPI.getType('category').then(r => setCategories(r.data));
    settingsAPI.getType('sub_category').then(r => setSubCategories(r.data));
    settingsAPI.getType('department').then(r => setDepts(r.data));
    import('axios').then(m => m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' }).get('/projects').then(r => setAllProjects(r.data)));
  }, [id]);

  useEffect(() => {
    if (!selectedVersion) return;
    trialsAPI.getAll(selectedVersion.id).then(r => {
      setTrials(r.data);
      if (r.data.length > 0) setSelectedTrial(r.data[r.data.length - 1]);
    });
  }, [selectedVersion?.id]);

  useEffect(() => {
    if (selectedTrial) trialsAPI.getMaterials(selectedTrial.id).then(r => setMaterials(r.data));
  }, [selectedTrial?.id]);

  const uploadTrialSheet = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedVersion) return toast.error('Select a version first');
    try {
      const r = await trialsAPI.uploadTrialSheet(selectedVersion.id, file);
      const summary = r.data.trials.map(t => `Trial ${t.trialNum}: ${t.items} items`).join(', ');
      toast.success(`Uploaded! ${summary}`);
      productsAPI.getVersions(id).then(r2 => setVersions(r2.data));
      trialsAPI.getAll(selectedVersion.id).then(r2 => { setTrials(r2.data); if (r2.data.length > 0) setSelectedTrial(r2.data[r2.data.length - 1]); });
    } catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); }
    e.target.value = '';
  };

  const addVersion = async () => {
    if (!versionForm.version_number) return toast.error('Version number required');
    await productsAPI.createVersion(id, versionForm);
    toast.success('Version added');
    setShowAddVersion(false);
    setVersionForm({ version_number: '', notes: '' });
    loadVersions();
  };

  const addTrial = async () => {
    if (!trialForm.trial_number || !selectedVersion) return toast.error('Trial number required');
    await trialsAPI.create(selectedVersion.id, trialForm);
    toast.success('Trial added');
    setShowAddTrial(false);
    setTrialForm({ trial_number: '', date: new Date().toISOString().split('T')[0], notes: '', status: 'ongoing' });
    trialsAPI.getAll(selectedVersion.id).then(r => { setTrials(r.data); if (r.data.length > 0) setSelectedTrial(r.data[r.data.length - 1]); });
  };

  const searchItems = async (q) => {
    setItemSearch(q);
    if (!q || q.length < 2) { setItemResults([]); return; }
    setSearchLoading(true);
    try {
      const r = await itemsAPI.search(q);
      setItemResults(r.data);
    } catch(e) {}
    setSearchLoading(false);
  };

  const handleItemSelect = (itemCode) => {
    const item = itemMaster.find(i => i.item_code === itemCode);
    if (item) setMaterialForm({ item_code: item.item_code, item_name: item.item_name, quantity: '', unit: item.unit || '', unit_cost: item.unit_cost || '' });
  };

  const addMaterial = async () => {
    if (!materialForm.item_code || !materialForm.quantity || !materialForm.unit_cost) return toast.error('Item, quantity and cost required');
    if (!selectedTrial) return toast.error('Select a trial first');
    await trialsAPI.addMaterial(selectedTrial.id, materialForm);
    toast.success('Material added');
    setShowAddMaterial(false);
    setMaterialForm({ item_code: '', item_name: '', quantity: '', unit: '', unit_cost: '' });
    trialsAPI.getMaterials(selectedTrial.id).then(r => setMaterials(r.data));
    productsAPI.getVersions(id).then(r => setVersions(r.data));
  };

  const deleteMaterial = async (matId) => {
    await trialsAPI.deleteMaterial(matId);
    toast.success('Removed');
    trialsAPI.getMaterials(selectedTrial.id).then(r => setMaterials(r.data));
    productsAPI.getVersions(id).then(r => setVersions(r.data));
  };

  const updateStatus = async (newStatus) => {
    await productsAPI.update(id, { ...product, status: newStatus });
    loadProduct();
    toast.success('Status updated');
  };

  const saveProductEdit = async () => {
    await productsAPI.update(id, editProductForm);
    if (editProductForm.project_id) {
      await productsAPI.move(id, editProductForm.project_id);
    }
    toast.success('Product updated');
    setShowEditProduct(false);
    loadProduct();
  };

  const doConvertToSfg = async () => {
    if (!convertMode) return;
    if (!window.confirm(convertMode === 'in_place'
      ? 'Convert this product to SFG in place? It keeps its recipe and trial data.'
      : 'Create an SFG copy?\n\nThe copy gets the RECIPE ONLY. Trials, cost sheet, taste sessions and photos STAY on this original product. Use "in place" if you want to keep everything together.')) return;
    try {
      const r = await productsAPI.convertToSfg(id, {
        mode: convertMode,
        link_fg_id: convertFgId || undefined,
        percentage: convertPct ? parseFloat(convertPct) : undefined
      });
      toast.success(convertMode === 'in_place' ? 'Converted to SFG' : 'SFG copy created');
      setShowEditProduct(false); setConvertMode(null); setConvertFgId(null); setConvertFgSearch(''); setConvertPct('');
      if (convertMode === 'copy' && r.data.sfg_id) { window.location.href = '/products/' + r.data.sfg_id; }
      else { window.location.reload(); }
    } catch (e) { toast.error(e.response?.data?.error || 'Convert failed'); }
  };

  const trialCost = materials.reduce((s, m) => s + (m.total_cost || 0), 0);

  if (!product) return <div className="empty-state"><p>Loading...</p></div>;

  return (
    <div>
      <div className="breadcrumb">
        <Link to="/projects">Projects</Link>
        <span className="breadcrumb-sep">›</span>
        <Link to={`/projects/${product.project_id}`}>{product.project_name}</Link>
        <span className="breadcrumb-sep">›</span>
        <span>{product.name}</span>
      </div>

      <div className="page-header">
        <div>
          <div className="page-title">{product.name}</div>
          <div className="page-subtitle">
            <span className="mono">{product.product_code}</span>
            <span> · {product.department}</span>
            <span> · Group: <span className="mono">{product.internal_group}</span></span>
            {product.product_type && <span> · <span style={{ color: 'var(--blue)' }}>{product.product_type}</span></span>}
            {parentFG && <span> · <span onClick={()=>window.location.href="/products/"+parentFG.product_id} style={{cursor:"pointer",color:"var(--accent)",fontWeight:600,background:"var(--accent-dim)",padding:"2px 8px",borderRadius:12,fontSize:11}}>↑ FG: {parentFG.name}</span></span>}
            {sfgLinks.length > 0 && sfgLinks.map(s => <span key={s.sfg_product_id}> · <span onClick={()=>window.location.href="/products/"+s.sfg_product_id} style={{cursor:"pointer",color:"var(--blue)",fontWeight:600,background:"rgba(59,130,246,0.1)",padding:"2px 8px",borderRadius:12,fontSize:11}}>↓ {s.sfg_name}</span></span>)}
          </div>
        </div>
        <div className="flex gap-2" style={{ alignItems: 'center' }}>
          <select value={product.status} onChange={e => updateStatus(e.target.value)} style={{ width: 'auto', padding: '6px 10px', fontSize: 12 }}>
            {productStatuses.map(s => <option key={s.id} value={s.value}>{STATUS_LABELS[s.value] || s.value}</option>)}
          </select>
          {product && !product.is_sfg && (
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowCreateSFG(true); setCreateSFGName(''); setCreateSFGPct(''); }}>+ SFG</button>
          )}
          {product && product.is_sfg === 1 && (
            <button className="btn btn-secondary btn-sm" onClick={() => { setShowLinkFG(true); setLinkFGSearch(''); setLinkFGResults([]); setLinkFGSelected(null); setLinkFGMode('share'); setLinkFGPct(''); }}>🔗 Link to FG / SFG</button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={async () => {
            try {
              const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
              const r = await API.get('/audit-log', { params: { entity_id: id, limit: 100 } });
              setHistoryLogs(r.data || []);
              setShowHistory(true);
            } catch { toast.error('Failed to load history'); }
          }}>🕘 History</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowEditProduct(true)}>Edit Product</button>
          <button className="btn btn-danger btn-sm" onClick={async () => {
            if (!window.confirm("Delete this product? It will move to Deleted Products and can be restored later.")) return;
            try {
              const API = (await import("axios")).default.create({ baseURL: process.env.NODE_ENV === "production" ? "/api" : "http://localhost:3001/api" });
              await API.delete(`/products/${id}/soft`);
              toast.success("Product deleted");
              window.location.href = "/products-list";
            } catch(e) { toast.error("Failed to delete"); }
          }}>🗑 Delete</button>
        </div>
      </div>

      <div className="tabs">
        <button className="btn btn-secondary btn-sm" style={{ marginRight: 10 }} onClick={() => setShowPdfDialog(true)}>⬇ Product PDF</button>
        <button className={`tab ${activeTab === 'trials' ? 'active' : ''}`} onClick={() => setActiveTab('trials')}>Trials & Costs</button>
        <button className={`tab ${activeTab === 'recipe' ? 'active' : ''}`} onClick={() => setActiveTab('recipe')}>Recipe {recipe ? '✓' : ''}</button>
        <button className={`tab ${activeTab === 'costsheet' ? 'active' : ''}`} onClick={() => setActiveTab('costsheet')}>Cost Sheet</button>
        <button className={`tab ${activeTab === 'nutrition' ? 'active' : ''}`} onClick={() => setActiveTab('nutrition')}>Nutrition</button>
        <button className={`tab ${activeTab === 'nutriscore' ? 'active' : ''}`} onClick={() => setActiveTab('nutriscore')}>Nutri-Score</button>
        <button className={`tab ${activeTab === 'flavor' ? 'active' : ''}`} onClick={() => setActiveTab('flavor')}>Flavor</button>
      </div>

      {activeTab === 'trials' && (
        <div>
          {/* Versions */}
          <div className="card mb-6">
            <div className="flex-between mb-4">
              <div className="card-title" style={{ margin: 0 }}>Versions</div>
            <div className="flex gap-2">
              {selectedVersion && (
                <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                  ↑ Upload Trial Sheet
                  <input type="file" accept=".xlsx,.xls" onChange={uploadTrialSheet} style={{ display: 'none' }} />
                </label>
              )}
              <button className="btn btn-secondary btn-sm" onClick={() => setShowAddVersion(true)}>+ Version</button>
            </div>
            </div>
            <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
              {versions.map(v => (
                <button key={v.id}
                  className={`btn ${selectedVersion?.id === v.id ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                  onClick={() => { setSelectedVersion(v); setSelectedTrial(null); setMaterials([]); }}>
                  {v.version_number}
                  <span className="mono" style={{ marginLeft: 6, opacity: 0.7 }}>
                    {v.trial_count}T · AED {(v.total_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 0 })}
                  </span>
                </button>
              ))}
              {versions.length === 0 && <span className="text-muted" style={{ fontSize: 13 }}>No versions yet — add one to start</span>}
            </div>
          </div>

          {selectedVersion && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button className={'btn btn-sm ' + (trialView==='detail' ? 'btn-primary' : 'btn-secondary')} onClick={() => setTrialView('detail')}>Detail</button>
              <button className={'btn btn-sm ' + (trialView==='matrix' ? 'btn-primary' : 'btn-secondary')} onClick={() => setTrialView('matrix')}>Matrix</button>
            </div>
          )}

          {trialView === 'matrix' && selectedVersion && (
            <div className="card" style={{ marginTop: 16 }}>
              <TrialMatrix productId={id} versions={versions} onRefresh={() => { loadVersions(); const sv = selectedVersion || versions[versions.length-1]; if (sv) trialsAPI.getAll(sv.id).then(r => setTrials(r.data)); }} />
            </div>
          )}

          {trialView === 'detail' && selectedVersion && (
            <div className="grid-2">
              {/* Trials */}
              <div className="card">
                <div className="flex-between mb-4">
                  <div className="card-title" style={{ margin: 0 }}>Trials — {selectedVersion.version_number}</div>
                  <div style={{display:"flex",gap:6}}><button className="btn btn-secondary btn-sm" onClick={()=>{const next=!showDeleted;setShowDeleted(next);if(next)trialsAPI.getDeleted(id).then(r=>setDeletedTrials(r.data));}}>{showDeleted?"Hide Deleted":"Show Deleted"}</button><button className="btn btn-secondary btn-sm" onClick={() => setShowAddTrial(true)}>+ Trial</button></div>
                </div>
                {trials.length === 0 ? (
                  <div className="empty-state" style={{ padding: 24 }}><p>No trials yet</p></div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {trials.map(t => (
                      <div key={t.id} onClick={() => setSelectedTrial(t)} style={{
                        padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
                        border: `1px solid ${selectedTrial?.id === t.id ? 'var(--accent)' : 'var(--border)'}`,
                        background: selectedTrial?.id === t.id ? 'var(--accent-dim)' : 'var(--bg-elevated)',
                        transition: 'all 0.15s',
                      }}>
                        <div className="flex-between">
                          <span style={{ fontWeight: 600 }}>Trial #{t.trial_number}</span>
                          <div style={{display:"flex",gap:6,alignItems:"center"}}><span className={`badge badge-${t.status}`}>{t.status}</span><button onClick={e=>{e.stopPropagation();if(window.confirm("Delete Trial #"+t.trial_number+"?"))trialsAPI.softDelete(t.id).then(()=>trialsAPI.getAll(selectedVersion.id).then(r=>setTrials(r.data)));}} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text-muted)",fontSize:14,padding:"0 2px"}} title="Delete">🗑</button></div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                          <span className="text-muted" style={{ fontSize: 12 }}>{t.date || '—'}</span>
                          <span className="cost-value mono" style={{ fontSize: 12 }}>
                            {t.material_count} items · AED {(t.total_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              {showDeleted && <div style={{marginTop:12,borderTop:"1px solid var(--border)",paddingTop:10}}><div style={{fontSize:11,fontWeight:700,color:"var(--text-muted)",marginBottom:6}}>DELETED TRIALS</div>{deletedTrials.length===0?<div style={{fontSize:12,color:"var(--text-muted)",padding:"8px 0"}}>No deleted trials</div>:deletedTrials.map(t=><div key={t.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 10px",borderRadius:6,background:"var(--bg-elevated)",marginBottom:4,opacity:0.7}}><span style={{fontSize:12}}>Trial #{t.trial_number} — {t.date||"—"}</span><button className="btn btn-secondary btn-sm" onClick={()=>trialsAPI.restore(t.id).then(()=>{trialsAPI.getAll(selectedVersion.id).then(r=>setTrials(r.data));trialsAPI.getDeleted(id).then(r=>setDeletedTrials(r.data));})}>Restore</button></div>)}</div>}
              </div>

              {/* Materials */}
              <div className="card">
                {selectedTrial ? (
                  <>
                    <div className="flex-between mb-4">
                      <div>
                        <div className="card-title" style={{ margin: 0 }}>Materials — Trial #{selectedTrial.trial_number}</div>
                        <div className="cost-value mono" style={{ fontSize: 13, marginTop: 4 }}>
                          Total: AED {trialCost.toLocaleString('en-AE', { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={() => setShowAddMaterial(true)}>+ Add Item</button>
                    </div>

                    {materials.length === 0 ? (
                      <div className="empty-state" style={{ padding: 24 }}>
                        <p>No materials yet. Click "+ Add Item" to add from item master.</p>
                      </div>
                    ) : (
                      <div className="table-wrap" style={{ maxHeight: 340, overflowY: 'auto' }}>
                        <table>
                          <thead>
                            <tr><th>Code</th><th>Name</th><th>Qty</th><th>Unit Cost</th><th className="text-right">Total</th><th></th></tr>
                          </thead>
                          <tbody>
                            {materials.map(m => (
                              <tr key={m.id}>
                                <td className="mono">{m.item_code}</td>
                                <td>{m.item_name || '—'}</td>
                                <td className="mono">{m.quantity} {m.unit}</td>
                                <td className="mono">AED {(m.unit_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 2 })}</td>
                                <td className="text-right cost-value">AED {(m.total_cost || 0).toLocaleString('en-AE', { maximumFractionDigits: 2 })}</td>
                                <td><button className="btn btn-danger btn-sm" onClick={() => deleteMaterial(m.id)}>×</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {materials.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <button className="btn btn-primary" onClick={() => setShowRecipeModal(true)}>✓ Confirm as Recipe</button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="empty-state"><p>Select a trial to view materials</p></div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'recipe' && (
        <RecipeTab recipe={recipe} productId={id} product={product} itemMaster={itemMaster} sfgLinks={sfgLinks} onUpdated={() => { productsAPI.getRecipe(id).then(r => setRecipe(r.data)); productsAPI.getRecipe(id).then(r => { if (r.data?.id) { import('axios').then(m => { const API = m.default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' }); API.get('/recipes/' + r.data.id + '/sfg-links').then(x => setSfgLinks(x.data || [])).catch(()=>{}); }); } }); }} />
      )}

      <ProductPdfDialog open={showPdfDialog} product={product || {}} recipe={recipe} sfgLinks={sfgLinks} onClose={() => setShowPdfDialog(false)} />
      {activeTab === 'flavor' && product && (
        <FlavorProfileTab product={product} />
      )}
      {activeTab === 'nutrition' && product && (
        <NutritionTab product={product} />
      )}
      {activeTab === 'nutriscore' && product && (
        <NutriScoreTab product={product} />
      )}
      {activeTab === 'costsheet' && product && (
        <CostSheet product={product} />
      )}

      {/* Add Version Modal */}
      {showAddVersion && (
        <div className="modal-overlay" onClick={() => setShowAddVersion(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Add Version</div><button className="modal-close" onClick={() => setShowAddVersion(false)}>×</button></div>
            <div className="form-grid">
              <div className="form-group"><label>Version Number *</label><input placeholder="e.g. V1, V2.1" value={versionForm.version_number} onChange={e => setVersionForm({ ...versionForm, version_number: e.target.value })} /></div>
              <div className="form-group full"><label>Notes</label><textarea placeholder="Changes from previous version..." value={versionForm.notes} onChange={e => setVersionForm({ ...versionForm, notes: e.target.value })} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddVersion(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addVersion}>Add Version</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Trial Modal */}
      {showAddTrial && (
        <div className="modal-overlay" onClick={() => setShowAddTrial(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Add Trial</div><button className="modal-close" onClick={() => setShowAddTrial(false)}>×</button></div>
            <div className="form-grid">
              <div className="form-group"><label>Trial Number *</label><input type="number" placeholder="1" value={trialForm.trial_number} onChange={e => setTrialForm({ ...trialForm, trial_number: e.target.value })} /></div>
              <div className="form-group"><label>Date</label><input type="date" value={trialForm.date} onChange={e => setTrialForm({ ...trialForm, date: e.target.value })} /></div>
              <div className="form-group">
                <label>Short Name (for code)</label>
                <input type="text" maxLength={6} value={editProductForm.short_name}
                  onChange={e => setEditProductForm({...editProductForm, short_name: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,'')})}
                  style={{ fontFamily: 'monospace' }} />
              </div>
              <div className="form-group full">
                <label>Final ERP Item Code (after commercial launch)</label>
                <input type="text" placeholder="e.g. FG-KARM-001 (fill after launch)" value={editProductForm.final_item_code}
                  onChange={e => setEditProductForm({...editProductForm, final_item_code: e.target.value})} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>This code will be assigned when the product is commercially launched in ERPNext</div>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={trialForm.status} onChange={e => setTrialForm({ ...trialForm, status: e.target.value })}>
                  <option value="ongoing">Ongoing</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              <div className="form-group full"><label>Notes</label><textarea placeholder="Trial notes..." value={trialForm.notes} onChange={e => setTrialForm({ ...trialForm, notes: e.target.value })} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddTrial(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addTrial}>Add Trial</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Material Modal */}
      {showAddMaterial && (
        <div className="modal-overlay" onClick={() => setShowAddMaterial(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Add Material — Trial #{selectedTrial?.trial_number}</div><button className="modal-close" onClick={() => setShowAddMaterial(false)}>×</button></div>
            <div className="form-grid">
              <div className="form-group full">
                <label>Select from Item Master *</label>
                <select value={materialForm.item_code} onChange={e => handleItemSelect(e.target.value)}>
                  <option value="">— Select item code —</option>
                  {itemMaster.map(i => <option key={i.id} value={i.item_code}>{i.item_code} — {i.item_name}</option>)}
                </select>
                {itemMaster.length === 0 && <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>No items in master. Go to Settings → Item Master to add items first.</div>}
              </div>
              {materialForm.item_code && (
                <>
                  <div className="form-group">
                    <label>Item Name</label>
                    <input value={materialForm.item_name} onChange={e => setMaterialForm({ ...materialForm, item_name: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Quantity *</label>
                    <input type="number" step="0.001" placeholder="0.00" value={materialForm.quantity} onChange={e => setMaterialForm({ ...materialForm, quantity: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Unit</label>
                    <input placeholder="kg, L, pcs" value={materialForm.unit} onChange={e => setMaterialForm({ ...materialForm, unit: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Unit Cost (AED ) *</label>
                    <input type="number" step="0.01" value={materialForm.unit_cost} onChange={e => setMaterialForm({ ...materialForm, unit_cost: e.target.value })} />
                  </div>
                  {materialForm.quantity && materialForm.unit_cost && (
                    <div className="form-group full">
                      <div style={{ padding: '10px 14px', background: 'var(--accent-dim)', borderRadius: 8, fontFamily: 'DM Mono, monospace', fontSize: 13 }}>
                        <span className="text-accent">Total: AED {(parseFloat(materialForm.quantity) * parseFloat(materialForm.unit_cost)).toLocaleString('en-AE', { maximumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddMaterial(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addMaterial}>Add Material</button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistory && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <div className="modal-header">
              <div className="modal-title">Change History</div>
              <button className="modal-close" onClick={() => setShowHistory(false)}>×</button>
            </div>
            {historyLogs.length === 0 ? (
              <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 13 }}>No recorded changes yet. (Tracking started when audit trail was enabled.)</div>
            ) : (
              <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                {historyLogs.map(l => {
                  const colors = { create:'#2e7d32', update:'#1a56db', delete:'#c62828', restore:'#b26a00', approve:'#2e7d32', reject:'#c62828', unlock:'#e67e00' };
                  let details = '';
                  try { const d = JSON.parse(l.details); details = Object.entries(d||{}).filter(([,v]) => v!=null && v!=='').map(([k,v]) => k+': '+v).join(' · '); } catch {}
                  return (
                    <div key={l.id} style={{ display:'flex', gap:12, padding:'10px 4px', borderBottom:'1px solid var(--border)' }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background: colors[l.action]||'#888', marginTop:5, flexShrink:0 }} />
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13 }}>
                          <strong>{l.username}</strong>
                          <span style={{ color: colors[l.action]||'#555', fontWeight:600, marginLeft:6, textTransform:'uppercase', fontSize:10, letterSpacing:'0.05em' }}>{l.action}</span>
                          <span style={{ color:'var(--text-muted)', marginLeft:6, fontSize:11 }}>{l.entity_type}</span>
                        </div>
                        {details && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{details}</div>}
                      </div>
                      <div style={{ fontSize:11, color:'var(--text-muted)', whiteSpace:'nowrap' }}>{new Date(l.created_at+'Z').toLocaleString('en-AE',{ day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' })}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create SFG Modal */}
      {showCreateSFG && (
        <div className="modal-overlay" onClick={() => setShowCreateSFG(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="modal-header">
              <div className="modal-title">Create New SFG</div>
              <button className="modal-close" onClick={() => setShowCreateSFG(false)}>×</button>
            </div>
            <div style={{ padding: '8px 12px', background: 'var(--bg-elevated)', borderRadius: 8, marginBottom: 14, fontSize: 12, color: 'var(--text-muted)' }}>
              Parent FG: <strong style={{ color: 'var(--text-primary)' }}>{product?.name}</strong>
              <span style={{ fontFamily: 'monospace', marginLeft: 6, color: 'var(--accent)', fontSize: 11 }}>{product?.product_code}</span>
            </div>
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label>SFG Name *</label>
              <input placeholder="e.g. Roasted Corn Salted" value={createSFGName} autoFocus onChange={e => setCreateSFGName(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label>Percentage in FG (%)</label>
              <input type="number" placeholder="e.g. 15 (can set later)" value={createSFGPct} onChange={e => setCreateSFGPct(e.target.value)} style={{ width: 160 }} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowCreateSFG(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!createSFGName.trim()} onClick={async () => {
                try {
                  const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
                  const r = await API.post(`/products/${id}/create-sfg`, {});
                  const sfg = r.data.sfg;
                  if (sfg?.id) {
                    await API.put(`/products/${sfg.id}`, { product_code: sfg.product_code, name: createSFGName.toUpperCase(), department: sfg.department, internal_group: sfg.internal_group, product_type: sfg.product_type, status: sfg.status, description: sfg.description || '', has_sfg: 0, category: sfg.category || '', sub_category: sfg.sub_category || '' });
                    if (parseFloat(createSFGPct) > 0) {
                      // Update the auto-created link with the percentage
                      const recipeR = await API.get(`/products/${id}/recipe`);
                      if (recipeR.data?.id) {
                        const linksR = await API.get(`/recipes/${recipeR.data.id}/sfg-links`);
                        const link = (linksR.data||[]).find(l => l.sfg_product_id === sfg.id);
                        if (link) await API.put(`/recipes/sfg-links/${link.id}`, { percentage: parseFloat(createSFGPct)||0, notes: '' });
                      }
                    }
                    toast.success('SFG created!');
                    setShowCreateSFG(false);
                    window.location.href = '/products/' + sfg.id;
                  }
                } catch(e) { toast.error('Failed to create SFG'); }
              }}>Create SFG</button>
            </div>
          </div>
        </div>
      )}

      {/* Link to FG Modal */}
      {showLinkFG && (
        <div className="modal-overlay" onClick={() => setShowLinkFG(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div className="modal-title">Link SFG to FG Product</div>
              <button className="modal-close" onClick={() => setShowLinkFG(false)}>×</button>
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label>Search FG Product</label>
              <input placeholder="Type name or code..." value={linkFGSearch} autoFocus
                onChange={async e => {
                  const v = e.target.value;
                  setLinkFGSearch(v);
                  setLinkFGSelected(null);
                  if (v.length < 2) { setLinkFGResults([]); return; }
                  try {
                    const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
                    const r = await API.get(`/products?search=${encodeURIComponent(v)}`);
                    setLinkFGResults((r.data || []).filter(p => String(p.id) !== String(id)).slice(0, 8));
                  } catch { setLinkFGResults([]); }
                }} />
            </div>
            {linkFGResults.length > 0 && !linkFGSelected && (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 220, overflowY: 'auto', marginBottom: 12 }}>
                {linkFGResults.map(p => (
                  <div key={p.id} onClick={() => { setLinkFGSelected(p); setLinkFGResults([]); setLinkFGSearch(p.product_code + ' — ' + p.name); }}
                    style={{ padding: '9px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--bg-elevated)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)', fontWeight: 700 }}>{p.product_code}</div>
                    <div style={{ fontWeight: 500 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.project_name || ''}</div>
                  </div>
                ))}
              </div>
            )}
            {linkFGSelected && (
              <>
                <div style={{ padding: '10px 14px', background: 'var(--accent-dim)', borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
                  Linking to: <strong>{linkFGSelected.product_code}</strong> — {linkFGSelected.name}
                </div>
                <div className="form-group" style={{ marginBottom: 10 }}>
                  <label>Link Mode</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', textTransform: 'none', fontWeight: 400, fontSize: 13 }}>
                      <input type="radio" checked={linkFGMode==='share'} onChange={() => setLinkFGMode('share')} style={{ width: 'auto', marginTop: 3 }} />
                      <span><strong>Use existing (shared)</strong><br/><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Same SFG shared — recipe changes reflect in all linked FGs</span></span>
                    </label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', cursor: 'pointer', textTransform: 'none', fontWeight: 400, fontSize: 13 }}>
                      <input type="radio" checked={linkFGMode==='copy'} onChange={() => setLinkFGMode('copy')} style={{ width: 'auto', marginTop: 3 }} />
                      <span><strong>Create a copy</strong><br/><span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Independent copy under the target FG — changes stay separate</span></span>
                    </label>
                  </div>
                </div>
                <div className="form-group" style={{ marginBottom: 14 }}>
                  <label>Percentage in FG (%)</label>
                  <input type="number" placeholder="e.g. 15" value={linkFGPct} onChange={e => setLinkFGPct(e.target.value)} style={{ width: 120 }} />
                </div>
              </>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowLinkFG(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={!linkFGSelected} onClick={async () => {
                try {
                  const API = (await import('axios')).default.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
                  await API.post(`/products/${id}/link-to-fg`, { fg_product_id: linkFGSelected.id, mode: linkFGMode, percentage: parseFloat(linkFGPct)||0 });
                  toast.success(linkFGMode === 'copy' ? 'SFG copied and linked!' : 'SFG linked!');
                  setShowLinkFG(false);
                  setTimeout(() => window.location.reload(), 600);
                } catch(e) { toast.error(e.response?.data?.error || 'Failed to link'); }
              }}>Link SFG</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Product Modal */}
      {showEditProduct && (
        <div className="modal-overlay" onClick={() => setShowEditProduct(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">Edit Product</div><button className="modal-close" onClick={() => setShowEditProduct(false)}>×</button></div>
            <div className="form-grid">
              <div className="form-group"><label>Product Code</label><input value={editProductForm.product_code} onChange={e => setEditProductForm({ ...editProductForm, product_code: e.target.value })} /></div>
              <div className="form-group"><label>Product Name</label><input value={editProductForm.name} onChange={e => setEditProductForm({ ...editProductForm, name: e.target.value })} /></div>
              <div className="form-group">
                <label>Department</label>
                <select value={editProductForm.department} onChange={e => setEditProductForm({ ...editProductForm, department: e.target.value })}>
                  {depts.map(d => <option key={d.id}>{d.value}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Internal Group</label><input value={editProductForm.internal_group} onChange={e => setEditProductForm({ ...editProductForm, internal_group: e.target.value })} /></div>
              <div className="form-group"><label>Category</label><select value={editProductForm.category||''} onChange={e => setEditProductForm({ ...editProductForm, category: e.target.value })}><option value="">Select category</option>{categories.map(c => <option key={c.id}>{c.value}</option>)}</select></div>
              <div className="form-group"><label>Sub Category</label><select value={editProductForm.sub_category||''} onChange={e => setEditProductForm({ ...editProductForm, sub_category: e.target.value })}><option value="">Select sub category</option>{subCategories.map(c => <option key={c.id}>{c.value}</option>)}</select></div>
              <div className="form-group">
                <label>Product Type</label>
                <select value={editProductForm.product_type} onChange={e => setEditProductForm({ ...editProductForm, product_type: e.target.value })}>
                  <option value="">Select type</option>
                  {productTypes.map(t => <option key={t.id}>{t.value}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Short Name (for code)</label>
                <input type="text" maxLength={6} value={editProductForm.short_name}
                  onChange={e => setEditProductForm({...editProductForm, short_name: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g,'')})}
                  style={{ fontFamily: 'monospace' }} />
              </div>
              <div className="form-group full">
                <label>Final ERP Item Code (after commercial launch)</label>
                <input type="text" placeholder="e.g. FG-KARM-001 (fill after launch)" value={editProductForm.final_item_code}
                  onChange={e => setEditProductForm({...editProductForm, final_item_code: e.target.value})} />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>This code will be assigned when the product is commercially launched in ERPNext</div>
              </div>
              <div className="form-group">
                <label>Status</label>
                <select value={editProductForm.status} onChange={e => setEditProductForm({ ...editProductForm, status: e.target.value })}>
                  {productStatuses.map(s => <option key={s.id} value={s.value}>{STATUS_LABELS[s.value] || s.value}</option>)}
                </select>
              </div>
              <div className="form-group full">
                <label>Move to Different Project</label>
                <select value={editProductForm.project_id || ''} onChange={e => setEditProductForm({ ...editProductForm, project_id: e.target.value })}>
                  <option value="">Keep current project</option>
                  {allProjects.map(p => <option key={p.id} value={p.id}>{p.name} ({p.project_code})</option>)}
                </select>
              </div>
              <div className="form-group full"><label>Description</label><textarea value={editProductForm.description} onChange={e => setEditProductForm({ ...editProductForm, description: e.target.value })} /></div>
            </div>
            {!product.is_sfg && (
              <div style={{ borderTop: '1px solid var(--border)', marginTop: 14, paddingTop: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Convert to SFG</div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <button className={`btn btn-sm ${convertMode === 'in_place' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setConvertMode(convertMode === 'in_place' ? null : 'in_place'); if (!allFgProducts.length) productsAPI.getAll().then(r => setAllFgProducts((r.data||[]).filter(p => !p.is_sfg && p.id !== parseInt(id)))); }}>
                    Convert in place (keep trials & recipe)
                  </button>
                  <button className={`btn btn-sm ${convertMode === 'copy' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setConvertMode(convertMode === 'copy' ? null : 'copy'); if (!allFgProducts.length) productsAPI.getAll().then(r => setAllFgProducts((r.data||[]).filter(p => !p.is_sfg && p.id !== parseInt(id)))); }}>
                    Create SFG copy
                  </button>
                </div>
                {convertMode && (
                  <div style={{ background: 'var(--bg-hover, #faf5fa)', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: 12, marginBottom: 6 }}>Optionally link the SFG to a parent FG:</div>
                    {convertFgId ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{convertFgName}</span>
                        <input type="number" className="form-input" style={{ width: 80 }} placeholder="%" value={convertPct} onChange={e => setConvertPct(e.target.value)} />
                        <button className="btn btn-secondary btn-sm" onClick={() => { setConvertFgId(null); setConvertFgName(''); }}>×</button>
                      </div>
                    ) : (
                      <div style={{ position: 'relative', marginBottom: 8 }}>
                        <input className="form-input" style={{ width: '100%' }} placeholder="Search FG product (optional)..."
                          value={convertFgSearch} onChange={e => setConvertFgSearch(e.target.value)} />
                        {convertFgSearch.length > 1 && (
                          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 40, maxHeight: 160, overflowY: 'auto' }}>
                            {allFgProducts.filter(p => (p.product_code + ' ' + p.name).toLowerCase().includes(convertFgSearch.toLowerCase())).slice(0, 8).map(p => (
                              <div key={p.id} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid var(--border)' }}
                                onClick={() => { setConvertFgId(p.id); setConvertFgName(p.name); setConvertFgSearch(''); }}>
                                <span className="mono" style={{ color: 'var(--accent)' }}>{p.product_code}</span> {p.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <button className="btn btn-primary btn-sm" onClick={doConvertToSfg}>
                      {convertMode === 'in_place' ? '⚙ Convert This Product to SFG' : '⚙ Create SFG Copy'}
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowEditProduct(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveProductEdit}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Modal */}
      {showRecipeModal && selectedTrial && (
        <RecipeModal productId={id} trial={selectedTrial} materials={materials}
          onClose={() => setShowRecipeModal(false)}
          onSaved={() => { setShowRecipeModal(false); productsAPI.getRecipe(id).then(r => setRecipe(r.data)); setActiveTab('recipe'); toast.success('Recipe confirmed!'); }} />
      )}
    </div>
  );
}
